import React from "react";
import splitArrayRole from "./utils/splitArrayRole";
import { SearchHit } from "../../../../creditSearch";

const setSplitAlignment = (text_alignment) => {
  switch (text_alignment) {
    case "left":
      return [" text-left ", " text-left "];
    case "justify":
      return [" text-left ", " text-right "];
    case "right":
      return [" text-right ", " text-right "];
    case "gutter":
      return [" text-right ", " text-left "];
    default:
      return [" text-right ", " text-left "];
  }
};

const getAlignment = (text_alignment, columns) => {
  switch (text_alignment) {
    case "left":
      return Array(columns).fill(" text-start ");
    case "justify":
      if (columns === 2) return [" text-start ", " text-end "];
      if (columns === 3) return [" text-start ", " text-center ", " text-end "];
      return [" text-start ", " text-end "];
    case "right":
      return Array(columns).fill(" text-end ");
    case "center":
      return Array(columns).fill(" text-center ");
    default:
      return Array(columns).fill(" text-end ");
  }
};

const getPadding = (textAlignment, noOfCols, colIdx, gutter) => {
  let pl = 0;
  let pr = 0;
  if (textAlignment === "gutter") {
    if (noOfCols === 2) {
      if (colIdx === 0) pr = gutter;
      if (colIdx === 1) pl = gutter;
    }
    if (noOfCols === 4) {
      if (colIdx === 1) pr = gutter;
      if (colIdx === 2) pl = gutter;
    }
  }
  return {
    paddingLeft: `${pl}px`,
    paddingRight: `${pr}px`,
  };
};

const roleText = (item) =>
  item?.role == null ? "" : String(item.role).trim();

const nameText = (item) =>
  item?.name == null ? "" : String(item.name).trim();

// A role row is any credit with a real role. Empty / "No Roll" is not a role.
const isRoleRow = (item) => {
  const role = roleText(item).toLowerCase();
  if (!role) return false;
  if (role === "no roll" || role === "." || role === ". .") return false;
  return true;
};

const isNameRow = (item) => Boolean(nameText(item));

// One group = one role + the name-only rows that follow it until the next role.
const groupByRole = (columnItems = []) => {
  const groups = [];
  for (const item of columnItems) {
    if (isRoleRow(item) || groups.length === 0) {
      groups.push([item]);
    } else {
      groups[groups.length - 1].push(item);
    }
  }
  return groups;
};

export default function RoleName2({ items, options, fontStyle, settings, isCard }) {
  // Drop rows that have NEITHER a role nor a name, so empty rows don't render as
  // blank gaps and don't count toward column balancing in the double-column
  // (gutter/left/right/justify) and ragged layouts.
  // const nonEmptyItems = (items || []).filter((item) => {
  //   const hasRole = item?.role != null && String(item.role).trim() !== "";
  //   const hasName = item?.name != null && String(item.name).trim() !== "";
  //   return hasRole || hasName;
  // });

  const roleCount = items.filter((item) => item.role);

  const visibleItems = (items || []).filter(
    (item) => isRoleRow(item) || isNameRow(item),
  );

  const arraySplitDataByRole = options?.double_columns
    ? splitArrayRole(visibleItems, options).ans
    : [];

  // ==========================================================================================

  const arraySplitData = options?.double_columns
    ? []
    : splitArrayRole(visibleItems, options).ans;
  const textAlignment = getAlignment(options?.text_alignment, options?.columns);

  const {
    lineHeight: _roleLineHeight,
    textDecoration: ___,
    marginBottom: _roleMb,
    ...roleStyle
  } = fontStyle.role;
  const {
    lineHeight: _nameLineHeight,
    textDecoration: ____,
    marginBottom: _nameMb,
    ...nameStyle
  } = fontStyle.name;

  const pairGapPx =
    Number(
      fontStyle?.name?.marginBottom ??
        fontStyle?.role?.marginBottom ??
        options?.row_gap ??
        0,
    ) || 0;

  const renderPairGroups = (columnItems, renderRow, namesFirst = false) =>
    groupByRole(columnItems).map((group, groupIdx) => {
      const roleItem = group.find(isRoleRow);
      const ordered = namesFirst
        ? [
            ...group.filter(isNameRow).map((item) => ({ ...item, role: null })),
            ...(roleItem ? [{ ...roleItem, name: null }] : []),
          ]
        : group;
      return (
      <div key={groupIdx} style={{ width: "100%" }}>
        {ordered.map(renderRow)}
        {roleItem && pairGapPx > 0 ? (
          <div
            style={{
              height: `${pairGapPx}px`,
              minHeight: `${pairGapPx}px`,
              width: "100%",
              flexShrink: 0,
              overflow: "hidden",
            }}
            aria-hidden
          />
        ) : null}
      </div>
      );
    });

  // Layout Line Height is a unitless point (1, 1.1, 1.2). Use it as CSS
  // line-height — not px, and not margin-bottom.
  const roleLh = Number(fontStyle?.role?.lineHeight) || 1;
  const nameLh = Number(fontStyle?.name?.lineHeight) || 1;

  const splitTextAlign = (() => {
    switch (options.text_alignment) {
      case "left":
        return ["left", "left"];
      case "right":
        return ["right", "right"];
      case "justify":
        return ["left", "right"];
      case "gutter":
      default:
        return ["right", "left"];
    }
  })();

  const cellTypeStyle = {
    verticalAlign: isCard ? "bottom" : "baseline",
    fontKerning: "none",
    fontFeatureSettings: '"kern" 0',
    textRendering: "geometricPrecision",
    WebkitFontSmoothing: "antialiased",
  };

  const tableGutterStyle = {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    borderSpacing: 0,
    marginTop: 0,
    marginLeft: "auto",
    marginRight: "auto",
    marginBottom: 0,
    wordBreak: "break-word",
  };

  const tableRaggedStyle = {
    borderCollapse: "collapse",
    borderSpacing: 0,
    marginTop: 0,
    marginLeft: "auto",
    marginRight: "auto",
    marginBottom: 0,
    wordBreak: "break-word",
  };

  const roleTextBoxStyle = {
    display: isCard ? "block" : "inline-block",
    maxWidth: "100%",
    verticalAlign: "baseline",
  };

  const nameTextBoxStyle = {
    display: isCard ? "block" : "inline-block",
    maxWidth: "100%",
    verticalAlign: "baseline",
  };

  const firstCellTextBox = options.name_on_left
    ? nameTextBoxStyle
    : roleTextBoxStyle;
  const secondCellTextBox = options.name_on_left
    ? roleTextBoxStyle
    : nameTextBoxStyle;

  const tdStyleRight = {
    width: "50%",
    textAlign: splitTextAlign[0],
    paddingRight: `${settings.gutter}px`,
    paddingLeft: "10px",
    ...(options.name_on_left ? nameStyle : roleStyle),
    ...cellTypeStyle,
    lineHeight: options.name_on_left ? nameLh : roleLh,
  };

  const tdStyleLeft = {
    width: "50%",
    textAlign: splitTextAlign[1],
    paddingLeft: `${settings.gutter}px`,
    paddingRight: "10px",
    ...(options.name_on_left ? roleStyle : nameStyle),
    ...cellTypeStyle,
    lineHeight: options.name_on_left ? roleLh : nameLh,
  };

  const tdStyleRightColumn = {
    textAlign: "right",
    paddingRight: `${settings?.ragged_gutter / 2}px`,
    paddingLeft: "10px",
    ...(options.name_on_left ? nameStyle : roleStyle),
    ...cellTypeStyle,
    lineHeight: options.name_on_left ? nameLh : roleLh,
  };

  const tdStyleLeftColumn = {
    textAlign: "left",
    paddingLeft: `${settings.ragged_gutter / 2}px`,
    paddingRight: "10px",
    ...(options.name_on_left ? roleStyle : nameStyle),
    ...cellTypeStyle,
    lineHeight: options.name_on_left ? roleLh : nameLh,
  };

  return options.double_columns ? (
    options.text_alignment === "ragged" ? (
      // Double Column (Ragged Align)

      <div
        className={`flex flex-row flex-wrap ${
          options?.columns === 1 ? "justify-center" : ""
        } 
        ${options?.text_alignment === "gutter" ? " justify-center " : ""}`}
        style={{
          width: `${options.column_width * options.columns}%`,
          margin: "0 auto",
          wordBreak: "break-word",
        }}
      >
        {arraySplitDataByRole?.map((items, index) => (
          <div
            key={index}
            className={`flex flex-col flex-1 break-words float-left`}
            style={{ width: `${options.column_width}%` }}
          >
            {renderPairGroups(items, (item, index) => (
                <table key={index} className="roleName_tr" style={tableRaggedStyle}>
                  <tbody>
                    <tr>
                      <td
                        className="roleName_td FontSIzeScale"
                        style={tdStyleRightColumn}
                      >
                        <span
                          style={{
                            ...firstCellTextBox,
                            textDecoration: options.name_on_left
                              ? fontStyle.name.textDecoration
                              : item.role == null
                              ? "none"
                              : fontStyle.role.textDecoration,
                            color: options.name_on_left
                              ? ""
                              : item.role == null
                              ? "transparent"
                              : fontStyle.role.color,
                          }}
                        >
                          {options.name_on_left
                            ? <SearchHit text={item.name} />
                            : item.role == null
                            ? ". "
                            : <SearchHit text={item.role} />}
                        </span>
                      </td>
                      <td
                        className="roleName_td FontSIzeScale"
                        style={tdStyleLeftColumn}
                      >
                        <span
                          style={{
                            ...secondCellTextBox,
                            textDecoration: options.name_on_left
                              ? item.role == null
                                ? "none"
                                : fontStyle.role.textDecoration
                              : fontStyle.name.textDecoration,
                            color: options.name_on_left
                              ? item.role == null
                                ? "transparent"
                                : fontStyle.role.color
                              : "",
                          }}
                        >
                          {options.name_on_left
                            ? item.role == null
                              ? " ."
                              : <SearchHit text={item.role} />
                            : <SearchHit text={item.name} />}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
            ))}
          </div>
        ))}
      </div>
    ) : (
      // Double Column (Gutter Align/Left/Right/Justify Align)

      <div
        className={`flex flex-row flex-wrap ${
          options?.columns === 1 ? "justify-center" : ""
        } 
        ${options?.text_alignment === "gutter" ? " justify-center " : ""}`}
        style={{
          width: `${options.column_width * options.columns}%`,
          margin: "0 auto",
          wordBreak: "break-word",
        }}
      >
        {arraySplitDataByRole?.map((items, index) => (
          <div
            key={index}
            className={`flex flex-col flex-1 break-words float-left`}
            style={{ width: `${options.column_width}%` }}
          >
            {renderPairGroups(items, (item, index) => (
                <table key={index} className="roleName_tr" style={tableGutterStyle}>
                  <tbody>
                    <tr>
                      <td
                        style={tdStyleRight}
                        className="roleName_td FontSIzeScale"
                      >
                        <span
                          style={{
                            ...firstCellTextBox,
                            textDecoration: options.name_on_left
                              ? fontStyle.name.textDecoration
                              : item.role == null
                              ? "none"
                              : fontStyle.role.textDecoration,
                            color: options.name_on_left
                              ? ""
                              : item.role == null
                              ? "transparent"
                              : fontStyle.role.color,
                            fontFamily: options.name_on_left
                              ? fontStyle.name.fontFamily
                              : fontStyle.role.fontFamily,
                          }}
                        >
                          {options.name_on_left
                            ? <SearchHit text={item.name} />
                            : item.role == null
                            ? "No Roll"
                            : <SearchHit text={item.role} />}
                        </span>
                      </td>
                      <td
                        style={tdStyleLeft}
                        className="roleName_td FontSIzeScale"
                      >
                        <span
                          style={{
                            ...secondCellTextBox,
                            textDecoration: options.name_on_left
                              ? item.role == null
                                ? "none"
                                : fontStyle.role.textDecoration
                              : fontStyle.name.textDecoration,
                            color: options.name_on_left
                              ? item.role == null
                                ? "transparent"
                                : fontStyle.role.color
                              : "",
                            fontFamily: options.name_on_left
                              ? fontStyle.role.fontFamily
                              : fontStyle.name.fontFamily,
                          }}
                        >
                          {options.name_on_left
                            ? item.role == null
                              ? "No Roll"
                              : <SearchHit text={item.role} />
                            : <SearchHit text={item.name} />}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
            ))}
          </div>
        ))}
      </div>
    )
  ) : (
    // Single Column (Gutter Align)

    <div
      className={`flex flex-row flex-wrap ${
        options?.columns === 1 ? "justify-center" : ""
      } 
      ${options?.text_alignment === "gutter" ? " justify-center " : ""}`}
      style={{
        width: `${options.column_width * options.columns}%`,
        margin: "0 auto",
        wordBreak: "break-word",
        justifyContent:
          options.text_alignment === "left"
            ? "flex-start"
            : options.text_alignment === "right"
            ? "flex-end"
            : "center",
      }}
    >
      {arraySplitData?.map((items, index) => (
        <div
          key={index}
          className={`flex flex-col flex-1 break-words float-left`}
        >
          {renderPairGroups(
            items,
            (item, index) => (
            <div
              key={index}
              className={`flex flex-col`}
              style={{
                alignItems:
                  options.text_alignment === "left"
                    ? "flex-start"
                    : options.text_alignment === "right"
                    ? "flex-end"
                    : "center",
              }}
            >
              <span
                className="FontSIzeScale"
                style={{
                  ...roleStyle,
                  textDecoration:
                    item.role == null ? "none" : fontStyle.role.textDecoration,
                  color:
                    item.role == null ? "transparent" : fontStyle.role.color,
                  lineHeight:
                    item.role == null ? "0" : fontStyle?.role?.lineHeight,
                  paddingLeft: "1px",
                  paddingRight: "1px",
                }}
              >
                {item.role == null ? "No Roll" : <SearchHit text={item.role} />}
              </span>
              <span
                className="FontSIzeScale"
                style={{
                  ...nameStyle,
                  textDecoration: fontStyle.name.textDecoration,
                  color:
                    item.name == null ? "transparent" : fontStyle.name.color,
                  lineHeight:
                    item.name == null ? "0" : fontStyle?.name?.lineHeight,
                  paddingLeft: "1px",
                  paddingRight: "1px",
                }}
              >
                {item.name == null ? "No Roll" : <SearchHit text={item.name} />}
              </span>
            </div>
            ),
            options.name_on_left,
          )}
        </div>
      ))}
    </div>
  );
}